<template>
  <v-app-bar app flat color="white" height="64" class="tn-topbar">
    <v-btn icon class="tn-topbar__toggle" :aria-label="toggleLabel" @click="$emit('toggle-nav')">
      <v-icon>{{ isDesktop && !mini ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
    </v-btn>

    <div class="tn-topbar__heading">
      <div v-if="crumbs.length" class="tn-topbar__crumbs">
        <template v-for="(crumb, i) in crumbs">
          <router-link v-if="crumb.to" :key="'c' + i" :to="crumb.to">{{ crumb.text }}</router-link>
          <span v-else :key="'c' + i">{{ crumb.text }}</span>
          <v-icon v-if="i < crumbs.length - 1" :key="'s' + i" size="14">mdi-chevron-right</v-icon>
        </template>
      </div>
      <h1 class="tn-topbar__title">{{ title }}</h1>
    </div>

    <v-spacer></v-spacer>

    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          v-bind="attrs"
          v-on="on"
          href="https://tanefer.com"
          target="_blank"
          rel="noopener"
          aria-label="Open the website"
          class="d-none d-sm-flex"
        >
          <v-icon size="22">mdi-open-in-new</v-icon>
        </v-btn>
      </template>
      <span>Open tanefer.com</span>
    </v-tooltip>

    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn icon v-bind="attrs" v-on="on" :to="{ name: 'notifications' }" aria-label="Notifications">
          <v-badge :value="unreadCount > 0" :content="unreadCount > 99 ? '99+' : unreadCount" color="orange" overlap offset-x="10" offset-y="10">
            <v-icon size="22">mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <span>Notifications</span>
    </v-tooltip>

    <div class="tn-topbar__divider d-none d-sm-block"></div>

    <v-menu v-if="user" offset-y left min-width="240" transition="slide-y-transition">
      <template v-slot:activator="{ on, attrs }">
        <button type="button" class="tn-user" v-bind="attrs" v-on="on" aria-label="Account menu">
          <v-avatar size="34" color="navy">
            <span class="tn-user__initials">{{ initials }}</span>
          </v-avatar>
          <span class="tn-user__name d-none d-md-inline">{{ user.name }}</span>
          <v-icon size="18" class="d-none d-md-inline-flex">mdi-chevron-down</v-icon>
        </button>
      </template>
      <v-card class="tn-user-menu">
        <div class="tn-user-menu__head">
          <div class="tn-user-menu__name">{{ user.name }}</div>
          <div v-if="user.email" class="tn-user-menu__email">{{ user.email }}</div>
        </div>
        <v-divider></v-divider>
        <v-list dense nav>
          <v-list-item :to="{ name: 'notifications' }">
            <v-list-item-icon><v-icon size="20">mdi-bell-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Notifications</v-list-item-title>
          </v-list-item>
          <v-list-item @click="$emit('logout')">
            <v-list-item-icon><v-icon size="20" color="error">mdi-logout</v-icon></v-list-item-icon>
            <v-list-item-title class="error--text">Log out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </v-app-bar>
</template>

<script>
export default {
  name: 'AppTopbar',
  props: {
    mini: { type: Boolean, default: false },
    unreadCount: { type: Number, default: 0 }
  },
  computed: {
    isDesktop () {
      return this.$vuetify.breakpoint.mdAndUp
    },
    toggleLabel () {
      if (!this.isDesktop) return 'Open menu'
      return this.mini ? 'Expand sidebar' : 'Collapse sidebar'
    },
    user () {
      return this.$store.state.user
    },
    initials () {
      const name = (this.user && this.user.name) || ''
      return name.split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || 'A'
    },
    title () {
      return this.$route.meta.title || 'Tanefer Admin'
    },
    crumbs () {
      const meta = this.$route.meta
      const crumbs = []
      if (meta.section) crumbs.push({ text: meta.section })
      if (meta.parent) {
        const parent = this.$router.resolve({ name: meta.parent }).route
        crumbs.push({ text: parent.meta.title, to: { name: meta.parent } })
      }
      return crumbs
    }
  }
}
</script>

<style lang="scss">
.tn-topbar.v-app-bar.v-toolbar.v-sheet {
  border-bottom: 1px solid var(--tn-border) !important;

  .v-toolbar__content {
    gap: 4px;
    padding: 0 20px 0 12px;
  }
}

.tn-topbar .v-btn--icon.v-btn--active::before {
  opacity: .06;
}

.tn-topbar__toggle {
  margin-right: 6px;
  color: var(--tn-muted) !important;
}

.tn-topbar__heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.tn-topbar__crumbs {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--tn-muted);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;

  a {
    color: var(--tn-muted) !important;
    text-decoration: none;

    &:hover {
      color: var(--v-primary-base) !important;
    }
  }

  .v-icon {
    color: #A0AEC0 !important;
  }
}

.tn-topbar__title {
  margin: 0;
  color: var(--tn-text);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -.01em;
  line-height: 1.35;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.tn-topbar__divider {
  width: 1px;
  height: 28px;
  margin: 0 10px 0 8px;
  background: var(--tn-border);
}

.tn-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 4px 4px;
  border-radius: 24px;
  color: var(--tn-text);
  transition: background .15s;

  &:hover,
  &:focus-visible {
    background: var(--tn-subtle);
    outline: none;
  }
}

.tn-user__initials {
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
}

.tn-user__name {
  max-width: 180px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
}

.tn-user-menu__head {
  padding: 14px 16px 12px;
}

.tn-user-menu__name {
  color: var(--tn-text);
  font-size: 14px;
  font-weight: 600;
}

.tn-user-menu__email {
  color: var(--tn-muted);
  font-size: 12.5px;
}
</style>
